/**
 * Garde d'une sous-page des Réglages.
 *
 * Un CGP qui arrive sur une page « Cabinet · admin » (lien direct, lien
 * « Compléter » d'un dossier) n'est pas renvoyé en silence : il voit pourquoi
 * la page lui est fermée, ce qui manque quand on le sait, et à quel admin le
 * demander. Les autres cas (section inconnue, page réservée aux CGP ouverte
 * par un admin) retombent sur `/reglages`.
 *
 * Ce n'est pas la frontière de sécurité — l'API refuse elle-même les
 * écritures (`require_permission`) — mais la page n'est jamais présentée.
 */

import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useLegalProfile } from "@/lib/api/legal-profile";
import { useAuth } from "@/lib/auth-context";

import { missingReason } from "./admin-only";
import { AdminOnlyPage } from "./admin-only-notice";
import { canOpenSettingsSection, findSettingsSection, type SettingsSlug } from "./settings-nav";

export function SettingsSectionGuard({
  slug,
  children,
}: {
  slug: SettingsSlug;
  children: ReactNode;
}) {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-ink-subtle" aria-label="Chargement" />
      </div>
    );
  }
  const isAdmin = user?.role === "ADMIN";
  if (canOpenSettingsSection(slug, isAdmin)) return <>{children}</>;
  if (!isAdmin && findSettingsSection(slug)?.audience === "admin") {
    return slug === "identite-reglementaire" ? <RegulatoryIdentityRefused /> : <AdminOnlyPage />;
  }
  return <Navigate to="/reglages" replace />;
}

/** Identité réglementaire : on dit ce qui bloque le DER (lecture permise au CGP). */
function RegulatoryIdentityRefused() {
  const { data } = useLegalProfile();
  const labels = (data?.missing ?? []).map((item) => item.label);
  return <AdminOnlyPage reason={missingReason("le DER", labels)} />;
}
